import React, { useEffect, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Link as LinkIcon, Unlink,
  AlignLeft, AlignCenter, AlignRight,
  Eraser, Code, Variable, ChevronDown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * RichTextEditor — TipTap-based WYSIWYG with optional variable-insert dropdown
 * and raw-HTML toggle. Drop-in replacement for a Textarea bound to HTML.
 *
 * Props:
 *   value           — string of HTML
 *   onChange(html)  — receives updated HTML
 *   variables       — optional array of variable names (without {{}}), used by
 *                     the Insert Variable dropdown.
 *   placeholder     — placeholder text when empty.
 *   minHeight       — px (default 220)
 *   testId          — root data-testid
 */
const COLORS = [
  { name: 'Default', value: '' },
  { name: 'Slate', value: '#475569' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Emerald', value: '#059669' },
  { name: 'Sky', value: '#0284c7' },
  { name: 'Indigo', value: '#4f46e5' },
];

const ToolbarBtn = ({ active, onClick, title, testId, children, disabled }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    disabled={disabled}
    data-testid={testId}
    className={`p-1.5 rounded-sm hover:bg-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? 'bg-slate-200 text-slate-900' : 'text-slate-600'
    }`}
  >
    {children}
  </button>
);

const Sep = () => <div className="w-px h-5 bg-slate-200 mx-0.5" />;

export const RichTextEditor = ({
  value = '',
  onChange,
  variables = [],
  placeholder = '',
  minHeight = 220,
  testId = 'rich-text-editor',
}) => {
  const [showHtml, setShowHtml] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-orange-600 underline', rel: 'noopener noreferrer' },
      }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class: 'prose-sm prose-slate max-w-none focus:outline-none px-3 py-2 min-h-[200px]',
        style: `min-height:${minHeight}px;`,
        'data-testid': `${testId}-content`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html);
    },
  });

  // Sync external value changes (e.g. switching templates) into the editor
  useEffect(() => {
    if (!editor) return;
    if (showHtml) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertVariable = useCallback(
    (name) => {
      if (!editor) return;
      editor.chain().focus().insertContent(`{{${name}}}`).run();
    },
    [editor]
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    // eslint-disable-next-line no-alert
    const url = window.prompt('Enter URL (leave blank to remove)', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: url, target: '_blank' })
      .run();
  }, [editor]);

  const setColor = useCallback(
    (color) => {
      if (!editor) return;
      if (!color) editor.chain().focus().unsetColor().run();
      else editor.chain().focus().setColor(color).run();
    },
    [editor]
  );

  const toggleHtml = () => {
    if (!showHtml) {
      setHtmlDraft(editor?.getHTML() || '');
      setShowHtml(true);
    } else {
      // Apply raw HTML back to editor
      editor?.commands.setContent(htmlDraft || '', false);
      onChange?.(htmlDraft || '');
      setShowHtml(false);
    }
  };

  if (!editor) return null;

  return (
    <div data-testid={testId} className="border border-slate-200 rounded-sm bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
        <ToolbarBtn
          testId={`${testId}-bold`}
          title="Bold (Ctrl+B)"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-italic`}
          title="Italic (Ctrl+I)"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-underline`}
          title="Underline (Ctrl+U)"
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn
          testId={`${testId}-h1`}
          title="Heading 1"
          active={editor.isActive('heading', { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          <Heading1 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-h2`}
          title="Heading 2"
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-h3`}
          title="Heading 3"
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn
          testId={`${testId}-bullet-list`}
          title="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-ordered-list`}
          title="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-blockquote`}
          title="Blockquote"
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Sep />
        <ToolbarBtn
          testId={`${testId}-link`}
          title="Insert / edit link"
          active={editor.isActive('link')}
          onClick={setLink}
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        {editor.isActive('link') && (
          <ToolbarBtn
            testId={`${testId}-unlink`}
            title="Remove link"
            onClick={() => editor.chain().focus().unsetLink().run()}
          >
            <Unlink className="w-3.5 h-3.5" />
          </ToolbarBtn>
        )}
        <Sep />
        <ToolbarBtn
          testId={`${testId}-align-left`}
          title="Align left"
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <AlignLeft className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-align-center`}
          title="Align center"
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <AlignCenter className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-align-right`}
          title="Align right"
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <AlignRight className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Sep />
        {/* Color picker */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              data-testid={`${testId}-color-trigger`}
              title="Text color"
              className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-600 flex items-center gap-0.5"
            >
              <span
                className="w-3.5 h-3.5 rounded-sm border border-slate-300"
                style={{ background: editor.getAttributes('textStyle').color || '#0f172a' }}
              />
              <ChevronDown className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-2" align="start">
            <div className="grid grid-cols-4 gap-1">
              {COLORS.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  onClick={() => setColor(c.value)}
                  data-testid={`${testId}-color-${c.name.toLowerCase()}`}
                  title={c.name}
                  className="w-7 h-7 rounded-sm border border-slate-200 flex items-center justify-center hover:ring-1 hover:ring-orange-400"
                >
                  <span
                    className="w-4 h-4 rounded-sm border border-slate-200"
                    style={{ background: c.value || '#0f172a' }}
                  />
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <ToolbarBtn
          testId={`${testId}-clear`}
          title="Clear formatting"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <Eraser className="w-3.5 h-3.5" />
        </ToolbarBtn>

        {variables.length > 0 && (
          <>
            <Sep />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  data-testid={`${testId}-variable-trigger`}
                  title="Insert template variable"
                  className="p-1.5 rounded-sm hover:bg-orange-50 text-orange-700 flex items-center gap-1 text-xs"
                >
                  <Variable className="w-3.5 h-3.5" />
                  <span className="hidden md:inline">Variable</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-1" align="start">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1">
                  Insert variable
                </div>
                <div className="max-h-56 overflow-auto">
                  {variables.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => insertVariable(v)}
                      data-testid={`${testId}-variable-${v}`}
                      className="w-full text-left text-xs px-2 py-1.5 rounded-sm hover:bg-orange-50 font-mono text-slate-700"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant={showHtml ? 'default' : 'outline'}
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleHtml}
            data-testid={`${testId}-html-toggle`}
            className="h-7 px-2 text-[11px] rounded-sm"
            title="Toggle raw HTML view"
          >
            <Code className="w-3 h-3 mr-1" /> {showHtml ? 'Done' : 'HTML'}
          </Button>
        </div>
      </div>

      {/* Body */}
      {showHtml ? (
        <textarea
          data-testid={`${testId}-html`}
          value={htmlDraft}
          onChange={(e) => setHtmlDraft(e.target.value)}
          className="w-full font-mono text-xs px-3 py-2 outline-none resize-y bg-slate-50"
          style={{ minHeight: `${minHeight}px` }}
          spellCheck={false}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
};

export default RichTextEditor;
